import React from 'react';
import Select, { GroupBase, components, OptionProps } from 'react-select';

interface GroupedOption {
  value: string;
  label: string;
  group: string;
}

interface GroupedSelectProps {
  options: GroupedOption[];
  value?: GroupedOption | null;
  onChange: (option: GroupedOption | null) => void;
  placeholder?: string;
  className?: string;
  isDisabled?: boolean;
  name?: string;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'fixed' | 'absolute';
}

// Custom Option component with enhanced styling
const CustomOption = (
  props: OptionProps<GroupedOption, false, GroupBase<GroupedOption>>,
) => {
  const { data, isFocused, isSelected } = props;

  return (
    <components.Option {...props}>
      <div
        className={`py-2 px-3 ${isFocused ? 'bg-blue-50' : ''} ${isSelected ? 'bg-blue-100' : ''}`}>
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-900">
            {data.label}
          </span>
        </div>
      </div>
    </components.Option>
  );
};

// Custom Group Header component
const CustomGroupHeader = ({ children, ...props }: any) => (
  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
      {children}
    </span>
  </div>
);

export const GroupedSelect: React.FC<GroupedSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className = '',
  isDisabled = false,
  name,
  menuPortalTarget,
  menuPosition,
}) => {
  // Group options by their group property
  const groupedOptions = options.reduce(
    (acc, option) => {
      const group = option.group || 'Other';
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push({
        value: option.value,
        label: option.label,
        group: option.group,
      });
      return acc;
    },
    {} as Record<string, GroupedOption[]>,
  );

  // Convert to react-select format
  const selectOptions = Object.entries(groupedOptions).map(
    ([groupName, groupOptions]) => ({
      label: groupName,
      options: groupOptions,
    }),
  );

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      minHeight: '40px',
      border: state.isFocused ? '1px solid #3b82f6' : '1px solid #d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        border: '1px solid #3b82f6',
      },
    }),
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      border: '1px solid #e5e7eb',
      borderRadius: '6px',
      boxShadow:
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 99999,
    }),
    groupHeading: (provided: any) => ({
      ...provided,
      padding: '8px 12px',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#374151',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#dbeafe'
        : state.isFocused
          ? '#eff6ff'
          : 'transparent',
      color: state.isSelected ? '#1e40af' : '#111827',
      padding: '8px 12px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#bfdbfe' : '#eff6ff',
        color: state.isSelected ? '#1e40af' : '#111827',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#111827',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: '#9ca3af',
    }),
  };

  return (
    <Select
      name={name}
      options={selectOptions}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isDisabled={isDisabled}
      className={className}
      styles={customStyles}
      components={{
        Option: CustomOption,
        GroupHeading: CustomGroupHeader,
      }}
      isClearable={false}
      isSearchable={true}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
    />
  );
};
